import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import { useRef, useState, type KeyboardEvent } from "react";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import AppDialogDefault from "@/components/ui/appDialogDefault/AppDialogDefault.component";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import { MIN_CARACTERES_LOOKUP_USUARIO } from "@/configs/system";

import DialogoSelecaoUsuarioResultados from "./DialogoSelecaoUsuarioResultados";

// --------------------------------- TYPES ---------------------------------
type DialogoSelecaoUsuarioProps = {
	readonly aberto: boolean;
	readonly termoBusca: string;
	readonly usuarios: ISpaceAuthLookupResponse[];
	readonly carregando: boolean;
	readonly erro: boolean;
	readonly aoAlterarBusca: (valor: string) => void;
	readonly aoSelecionar: (usuario: ISpaceAuthLookupResponse) => void;
	readonly aoFechar: () => void;
};

// -------------------------------- HELPERS --------------------------------
function obterSubtitulo(carregando: boolean, quantidade: number): string {
	if (carregando) return "Pesquisando usuários…";
	if (quantidade === 1) return "1 usuário encontrado";
	return `${quantidade} usuários encontrados`;
}

// ------------------------------- COMPONENT -------------------------------
export default function DialogoSelecaoUsuario({
	aberto,
	termoBusca,
	usuarios,
	carregando,
	erro,
	aoAlterarBusca,
	aoSelecionar,
	aoFechar,
}: Readonly<DialogoSelecaoUsuarioProps>) {
	// -------------------------------- STATES --------------------------------
	const [indiceAtivo, setIndiceAtivo] = useState(0);
	const referenciasUsuarios = useRef<Array<HTMLDivElement | null>>([]);

	// ----------------------------- DERIVED STATE -----------------------------
	const atingiuTamanhoMinimo = termoBusca.trim().length >= MIN_CARACTERES_LOOKUP_USUARIO;
	const podeNavegar = atingiuTamanhoMinimo && !carregando && !erro && usuarios.length > 0;
	const indiceAtivoValido = podeNavegar ? Math.min(indiceAtivo, usuarios.length - 1) : -1;

	const subtitulo = obterSubtitulo(carregando, usuarios.length);

	// ------------------------------- HANDLERS --------------------------------
	function focarUsuario(indice: number) {
		setIndiceAtivo(indice);
		referenciasUsuarios.current[indice]?.focus();
	}

	function selecionarUsuario(usuario: ISpaceAuthLookupResponse) {
		setIndiceAtivo(0);
		aoSelecionar(usuario);
	}

	function handleBuscaKeyDown(evento: KeyboardEvent<HTMLElement>) {
		if (!podeNavegar) return;

		if (evento.key === "ArrowDown" || evento.key === "ArrowUp") {
			evento.preventDefault();
			focarUsuario(evento.key === "ArrowDown" ? 0 : usuarios.length - 1);
			return;
		}

		if (evento.key === "Enter" && indiceAtivoValido >= 0) {
			evento.preventDefault();
			selecionarUsuario(usuarios[indiceAtivoValido]);
		}
	}

	function handleUsuarioKeyDown(evento: KeyboardEvent<HTMLElement>, indice: number) {
		if (evento.key !== "ArrowDown" && evento.key !== "ArrowUp") return;

		evento.preventDefault();
		const direcao = evento.key === "ArrowDown" ? 1 : -1;
		const proximoIndice = (indice + direcao + usuarios.length) % usuarios.length;
		focarUsuario(proximoIndice);
	}

	function handleAlterarBusca(valor: string) {
		setIndiceAtivo(0);
		aoAlterarBusca(valor.toUpperCase());
	}

	function handleFechar() {
		setIndiceAtivo(0);
		aoFechar();
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<AppDialogDefault
			aberto={aberto}
			onFechar={handleFechar}
			titulo="Selecionar usuário"
			subtitulo={atingiuTamanhoMinimo ? subtitulo : undefined}
			icone="lucide:users"
			maxWidth="sm"
			exibirRodape={false}
			bodyClassName="overflow-hidden"
			paperClassName="max-h-[70vh]"
		>
			<div className="flex min-h-0 flex-1 flex-col gap-3 bg-muted p-4">
				<TextField
					autoFocus
					fullWidth
					size="small"
					value={termoBusca}
					onChange={(evento) => handleAlterarBusca(evento.target.value)}
					placeholder="Digite o nome ou ID do usuário"
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<FuseSvgIcon size={18}>lucide:search</FuseSvgIcon>
								</InputAdornment>
							),
							endAdornment: carregando ? <CircularProgress size={18} /> : undefined,
						},
						htmlInput: {
							"aria-label": "Pesquisar usuário para entrar",
							onKeyDown: handleBuscaKeyDown,
							maxLength: 30,
						},
					}}
				/>

				<section className="min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
					<DialogoSelecaoUsuarioResultados
						dados={{ atingiuTamanhoMinimo, usuarios, indiceAtivo: indiceAtivoValido }}
						carregando={carregando}
						erro={erro}
						acoes={{
							aoRegistrarReferencia: (indice, elemento) => {
								referenciasUsuarios.current[indice] = elemento;
							},
							aoAtivar: setIndiceAtivo,
							aoTeclaUsuario: handleUsuarioKeyDown,
							aoSelecionar: selecionarUsuario,
						}}
					/>
				</section>
			</div>
		</AppDialogDefault>
	);
}
